import React from 'react';

import Button from '@mui/material/Button';
import { useFormik } from 'formik';
import ReCAPTCHA from 'react-google-recaptcha';
import { useNavigate } from 'react-router-dom';
import { useSetRecoilState } from 'recoil';
import { hasAdminAccess, passwordSignedIn, useAuth } from 'utils/auth';

import TextInput from 'components/TextInput';
import { errorMessage } from 'pages/Login/state';
import api, { continueWithEmail } from 'utils/api';
import useSsoLabel from 'utils/useSsoLabel';

// =============================================================================
export default function Form() {
  const auth = useAuth();
  const [passwordStep, setPasswordStep] = React.useState(false);
  const [redirecting, setRedirecting] = React.useState(false);
  const setSnackbarMessage = useSetRecoilState(errorMessage);
  const [captchaDone, setCaptchaDone] = React.useState(false);
  const navigate = useNavigate();

  React.useEffect(() => {
    return () => {
      formik.handleReset(null);
    };
  }, []);

  const formik = useFormik({
    initialValues: {
      email: '',
      password: ''
    },
    onSubmit: async (values) => {
      if (!values.email.trim() || (passwordStep && !values.password)) {
        setSnackbarMessage('Fields cannot be empty!');
        return;
      }
      try {
        const email = values.email.trim().toLowerCase();
        if (!passwordStep) {
          if (await continueWithEmail(email)) {
            await formik.setFieldValue('email', email);
            setPasswordStep(true);
          } else {
            setRedirecting(true);
          }
          return;
        }
        const res = await api.post('/public/login', {
          email,
          password: values.password
        });
        passwordSignedIn(res.data);
      } catch (err: any) {
        setSnackbarMessage(
          !passwordStep
            ? 'Unable to check your email. Please try again.'
            : 'Wrong email or password!'
        );
      }
    }
  });

  const sso = useSsoLabel(formik.values.email, !passwordStep);

  React.useEffect(() => {
    if (hasAdminAccess(auth.user)) navigate('/', { replace: true });
    else if (auth.user)
      setSnackbarMessage('You do not have permission to access this website.');
  }, [auth.user, navigate, setSnackbarMessage]);

  return (
    <div>
      <form
        onSubmit={formik.handleSubmit}
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          margin: '40px 0'
        }}
      >
        <TextInput
          variant='outlined'
          id='email'
          name='email'
          type='email'
          readOnly={passwordStep}
          label='Email'
          placeholder='email@domain.com'
          value={formik.values.email}
          dataCy='email'
          onChange={formik.handleChange}
          error={formik.touched.email && Boolean(formik.errors.email)}
          helperText={formik.touched.email && formik.errors.email}
          containerStyle={{ width: '100%', maxWidth: 400 }}
        />
        {passwordStep && (
          <TextInput
            variant='outlined'
            id='password'
            name='password'
            label='Password'
            type='password'
            placeholder='**********'
            value={formik.values.password}
            dataCy='password'
            onChange={formik.handleChange}
            error={formik.touched.password && Boolean(formik.errors.password)}
            helperText={formik.touched.password && formik.errors.password}
            containerStyle={{ width: '100%', maxWidth: 400 }}
          />
        )}
        {passwordStep && (
          <Button
            disabled={formik.isSubmitting}
            onClick={() => {
              setPasswordStep(false);
              formik.setFieldValue('password', '');
            }}
          >
            Sign in with a different email address
          </Button>
        )}

        <div style={{ height: 16 }} />
        {!!import.meta.env.RECAPTCHA_SECRET_KEY && (
          <ReCAPTCHA
            sitekey={import.meta.env.RECAPTCHA_CLIENT_KEY}
            onChange={() => setCaptchaDone(true)}
          />
        )}
        <div style={{ height: 16 }} />

        <Button
          type='submit'
          variant='primary'
          color='dark'
          disabled={
            !auth.ready ||
            formik.isSubmitting ||
            redirecting ||
            (!captchaDone && !!import.meta.env.RECAPTCHA_SECRET_KEY)
          }
          data-cy='login-button'
        >
          {redirecting
            ? 'Redirecting…'
            : passwordStep
              ? 'Login'
              : sso
                ? 'Continue with SSO'
                : 'Continue'}
        </Button>
      </form>
    </div>
  );
}
